import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import { TranslatePipe } from '@core/i18n-legacy';
import type { TimelineEvent, TimelineToolEvent } from '@core/tellent-assistant-util';
import { ButtonComponent, IconComponent, SpinnerComponent } from '@core/user-interface-legacy';

import { TellentAssistantBrandMarkComponent } from '../../brand-mark/tellent-assistant-brand-mark.component';
import { TellentAssistantTextPartComponent } from '../text-part/text-part.component';

export type ThinkingRunState = 'running' | 'done';

interface TimelineReasoningRow {
  kind: 'reasoning';
  key: string;
  content: string;
  done: boolean;
}

/** Run of adjacent tool calls that share a title and a status, rendered as one row. */
interface TimelineToolGroupRow {
  kind: 'tool-group';
  key: string;
  /** Resolved tool title; `null` when the tool has none and the generic label is shown. */
  title: string | null;
  status: TimelineToolEvent['status'];
  count: number;
}

type TimelineRow = TimelineReasoningRow | TimelineToolGroupRow;

@Component({
  selector: 'tellent-assistant-thinking-section',
  imports: [
    ButtonComponent,
    IconComponent,
    SpinnerComponent,
    TellentAssistantBrandMarkComponent,
    TellentAssistantTextPartComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './thinking-section.component.html',
  styleUrl: './thinking-section.component.less',
})
export class TellentAssistantThinkingSectionComponent {
  protected readonly expanded = signal<boolean>(false);

  protected readonly toolCount = computed(
    () => this.timeline().filter(event => event.kind === 'tool').length,
  );

  protected readonly hasContent = computed(() => this.timeline().length > 0);

  protected readonly rows = computed<readonly TimelineRow[]>(() => {
    const titles = this.toolTitles();
    const rows: TimelineRow[] = [];

    for (const event of this.timeline()) {
      if (event.kind === 'reasoning') {
        rows.push({
          kind: 'reasoning',
          key: `reasoning-${rows.length}`,
          content: event.content,
          done: event.done,
        });
        continue;
      }

      const title = titles[event.name] ?? null;
      const previous = rows[rows.length - 1];
      if (
        previous?.kind === 'tool-group' &&
        previous.title === title &&
        previous.status === event.status
      ) {
        previous.count += 1;
        continue;
      }

      rows.push({
        kind: 'tool-group',
        key: event.toolCallId,
        title,
        status: event.status,
        count: 1,
      });
    }

    return rows;
  });

  protected readonly latestRunningTool = computed(() => {
    const events = this.timeline();
    const last = events[events.length - 1];
    return last?.kind === 'tool' && last.status === 'running' ? last : undefined;
  });

  protected readonly triggerLabelKey = computed(() => {
    if (this.runState() === 'done') return 'tlc.tellent_assistant.tool_call.steps_completed';
    const tool = this.latestRunningTool();
    if (!tool) return 'tlc.tellent_assistant.tool_call.thinking_status';
    return this.toolTitles()[tool.name] ? null : 'tlc.tellent_assistant.tool_call.working_status';
  });

  protected readonly triggerLabelToolName = computed(() => {
    const tool = this.latestRunningTool();
    if (!tool) return null;
    return this.toolTitles()[tool.name] ?? null;
  });

  protected readonly showThinkingShimmer = computed(() => {
    if (this.runState() !== 'running') return false;
    return !this.latestRunningTool();
  });

  public readonly timeline = input.required<readonly TimelineEvent[]>();
  public readonly runState = input.required<ThinkingRunState>();
  public readonly toolTitles = input<Record<string, string>>({});

  protected toggle(): void {
    this.expanded.update(v => !v);
  }
}
